import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalizeWords(value: string): string {
  return value.replace(
    /(^|[\s-])(\S)/g,
    (_match, boundary: string, letter: string) =>
      `${boundary}${letter.toLocaleUpperCase()}`,
  );
}

export type AwardShowFallbackIconName =
  | "music"
  | "clapperboard"
  | "tv"
  | "star"
  | "theater"
  | "film"
  | "award"
  | "trophy";

export function normalizeAwardShowImageUrl(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:") {
      return null;
    }
    return trimmed;
  } catch {
    return null;
  }
}

export function awardShowImagePreviewUrl(
  localPreviewUrl: string | null | undefined,
  imageUrl: string | null | undefined,
): string | null {
  const local = localPreviewUrl?.trim();
  if (local) {
    return local;
  }
  return normalizeAwardShowImageUrl(imageUrl);
}

export const AWARD_SHOW_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

const ALLOWED_AWARD_SHOW_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
]);

export function validateAwardShowImageFile(file: {
  type: string;
  size: number;
}): { ok: true } | { ok: false; error: string } {
  if (file.size === 0) {
    return { ok: false, error: "Poster image is empty" };
  }

  if (file.size > AWARD_SHOW_IMAGE_MAX_BYTES) {
    return {
      ok: false,
      error: `Poster image must be ${AWARD_SHOW_IMAGE_MAX_BYTES / (1024 * 1024)}MB or smaller`,
    };
  }

  const mimeType = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (!ALLOWED_AWARD_SHOW_IMAGE_MIME_TYPES.has(mimeType)) {
    return {
      ok: false,
      error: "Poster image must be jpeg, png, webp, gif, or avif",
    };
  }

  return { ok: true };
}

export function isLikelyHostedAwardShowImageUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return (
      hostname === "firebasestorage.googleapis.com" ||
      hostname === "storage.googleapis.com" ||
      hostname.endsWith(".firebasestorage.app")
    );
  } catch {
    return false;
  }
}

export function nomineeImageNeedsHosting(
  imageUrl: string | null | undefined,
): boolean {
  if (!imageUrl?.trim()) {
    return false;
  }

  return imageUrl.split(",").some((part) => {
    const trimmed = part.trim();
    if (!trimmed) {
      return false;
    }
    const normalized = normalizeAwardShowImageUrl(trimmed);
    if (!normalized) {
      return true;
    }
    return !isLikelyHostedAwardShowImageUrl(normalized);
  });
}

export function getAwardShowFallbackIconName(
  name: string,
): AwardShowFallbackIconName {
  const lower = name.toLowerCase();

  if (
    lower.includes("grammy") ||
    lower.includes("music") ||
    lower.includes("mtv")
  ) {
    return "music";
  }
  if (lower.includes("emmy") || lower.includes("television")) {
    return "tv";
  }
  if (lower.includes("oscar") || lower.includes("academy")) {
    return "clapperboard";
  }
  if (lower.includes("golden globe")) {
    return "star";
  }
  if (
    lower.includes("tony") ||
    lower.includes("theatre") ||
    lower.includes("theater") ||
    lower.includes("sag") ||
    lower.includes("screen actor")
  ) {
    return "theater";
  }
  if (lower.includes("critic")) {
    return "award";
  }
  if (lower.includes("bafta") || lower.includes("film")) {
    return "film";
  }

  return "trophy";
}

export function formatDistanceToNow(date: Date | string): string {
  const now = new Date();
  const target = date instanceof Date ? date : new Date(date);
  const diff = target.getTime() - now.getTime();

  if (diff < 0) {
    return "expired";
  }

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 0) {
    return `${days} day${days !== 1 ? "s" : ""}`;
  }
  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }

  return "less than a minute";
}

export type AwardShowResultsStatus = "pending" | "in-progress" | "complete";

export function getAwardShowResultsStatus(
  announcedResultsCount: number,
  totalCategories: number,
): AwardShowResultsStatus {
  if (totalCategories > 0 && announcedResultsCount >= totalCategories) {
    return "complete";
  }

  if (announcedResultsCount > 0) {
    return "in-progress";
  }

  return "pending";
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/**
 * Format a date with timezone conversion
 * Shows the time in the user's local timezone
 */
export function formatDateWithTimezone(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

export function isPartySoftDeleted(
  deletedAt: Date | string | null | undefined,
): boolean {
  if (deletedAt == null) {
    return false;
  }
  if (typeof deletedAt === "string") {
    return deletedAt.trim() !== "";
  }
  return true;
}

export function formatPartyDeadline(
  date: Date,
  now: Date = new Date(),
): string {
  const sameYear = date.getFullYear() === now.getFullYear();
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

  return `${datePart} at ${timePart}`;
}

/**
 * Common timezones for award shows
 */
export const COMMON_TIMEZONES = [
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
] as const;

/**
 * Convert a datetime-local value (YYYY-MM-DDTHH:mm) + timezone to a UTC ISO string.
 * Used by web and mobile create-party forms.
 *
 * Strategy: treat the local time as a "naive" UTC timestamp, then format it
 * in the target timezone to read back the actual local representation. The
 * difference between the two is the UTC offset. One correction pass handles
 * DST boundaries where the offset changes between the naive estimate and the
 * true UTC instant.
 */
export function dateTimeLocalToUTCString(
  dateTimeLocal: string,
  timezone: string,
): string {
  const parts = dateTimeLocal.split("T");
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return new Date(dateTimeLocal).toISOString();
  }

  const [year = 0, month = 1, day = 1] = parts[0].split("-").map(Number);
  const [hours = 0, minutes = 0] = parts[1].split(":").map(Number);

  // Naive UTC: the local date/time values treated as if they were UTC.
  const naiveMs = Date.UTC(year, month - 1, day, hours, minutes);

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  // Re-interpret a UTC timestamp as the timezone's local time expressed in ms.
  const toLocalMs = (ts: number): number => {
    const p = formatter.formatToParts(new Date(ts));
    const get = (type: string) =>
      Number(p.find((x) => x.type === type)?.value ?? 0);
    const hour = get("hour") % 24;
    return Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      hour,
      get("minute"),
    );
  };

  // At naiveMs, the timezone displays (naiveMs + offsetMs). The true UTC is
  // naiveMs - offsetMs = 2*naiveMs - toLocalMs(naiveMs).
  let utcMs = 2 * naiveMs - toLocalMs(naiveMs);

  // One correction pass: if the offset changed across a DST boundary between
  // naiveMs and utcMs, shift the estimate by the residual error rather than
  // re-anchoring against naiveMs (which oscillates when the boundary lies
  // between naiveMs and the first estimate).
  if (toLocalMs(utcMs) !== naiveMs) {
    utcMs = utcMs + (naiveMs - toLocalMs(utcMs));
  }

  return new Date(utcMs).toISOString();
}

/**
 * Convert a UTC date to a datetime-local string (YYYY-MM-DDTHH:mm) in the given timezone.
 */
export function utcToDateTimeLocal(date: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const normalizedHour = String(Number(get("hour")) % 24).padStart(2, "0");

  return `${get("year")}-${get("month")}-${get("day")}T${normalizedHour}:${get("minute")}`;
}

// ─── Personal highlight context ─────────────────────────────

export interface PersonalHighlightContext {
  userId: string;
  userName: string;
  rank: number;
  correctCount: number;
  totalCategories: number;
  accuracy: number;
  isTopPerformer: boolean;
  isBottomPerformer: boolean;
  exclusiveHitCategoryNames: string[];
  bestFieldName: string | null;
  bestFieldCorrectCount: number;
  isSwingPickPlayer: boolean;
  swingCategoryName: string | null;
  swingImpact: string | null;
  comebackData: {
    worstRank: number;
    finalRank: number;
    worstAfterCategory: string;
  } | null;
}

export function calculateAverageAccuracy(
  leaderboard: Array<{ accuracy: number }>,
): number {
  if (leaderboard.length === 0) {
    return 0;
  }

  const total = leaderboard.reduce((sum, player) => sum + player.accuracy, 0);
  return Math.round((total / leaderboard.length) * 100);
}

export function calculateRankPercentile(
  rank: number,
  participantCount: number,
): number {
  if (participantCount <= 1) {
    return 100;
  }

  const normalized = (participantCount - rank) / (participantCount - 1);
  return Math.max(0, Math.min(100, Math.round(normalized * 100)));
}

export function assignDenseRanks<T>(
  items: T[],
  getScore: (item: T) => number,
): Array<T & { rank: number }> {
  let previousScore: number | null = null;
  let currentRank = 0;

  return items.map((item) => {
    const score = getScore(item);

    if (previousScore === null || score !== previousScore) {
      currentRank += 1;
      previousScore = score;
    }

    return {
      ...item,
      rank: currentRank,
    };
  });
}

export function gatherPersonalHighlightContext(
  recap: {
    leaderboard: Array<{
      userId: string;
      userName: string;
      correctCount: number;
      totalCategories: number;
      rank: number;
      accuracy: number;
      bestFieldName: string | null;
      bestFieldCorrectCount: number;
      exclusiveHitCategoryNames: string[];
    }>;
    topPerformer: { userId: string } | null;
    bottomPerformer: { userId: string } | null;
    biggestSwing: {
      userId: string;
      categoryName: string;
      impact: string;
    } | null;
    timeline?: {
      biggestComeback: {
        userId: string;
        worstRank: number;
        finalRank: number;
        worstAfterCategory: string;
      } | null;
    } | null;
  },
  userId: string,
): PersonalHighlightContext | null {
  const player = recap.leaderboard.find((e) => e.userId === userId);
  if (!player) return null;

  return {
    userId: player.userId,
    userName: player.userName,
    rank: player.rank,
    correctCount: player.correctCount,
    totalCategories: player.totalCategories,
    accuracy: player.accuracy,
    isTopPerformer: recap.topPerformer?.userId === player.userId,
    isBottomPerformer: recap.bottomPerformer?.userId === player.userId,
    exclusiveHitCategoryNames: player.exclusiveHitCategoryNames,
    bestFieldName: player.bestFieldName,
    bestFieldCorrectCount: player.bestFieldCorrectCount,
    isSwingPickPlayer: recap.biggestSwing?.userId === player.userId,
    swingCategoryName:
      recap.biggestSwing?.userId === player.userId
        ? recap.biggestSwing.categoryName
        : null,
    swingImpact:
      recap.biggestSwing?.userId === player.userId
        ? recap.biggestSwing.impact
        : null,
    comebackData:
      recap.timeline?.biggestComeback?.userId === player.userId
        ? {
            worstRank: recap.timeline.biggestComeback.worstRank,
            finalRank: recap.timeline.biggestComeback.finalRank,
            worstAfterCategory:
              recap.timeline.biggestComeback.worstAfterCategory,
          }
        : null,
  };
}

/** English product-email fallback when a recipient has no usable name. */
export const EMAIL_TEMPLATE_NAME_FALLBACK = "there";

function firstNonEmptyToken(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.split(/\s+/).find(Boolean) ?? null;
}

export function fullNameOrThere(
  name: string | null | undefined,
): string {
  const trimmed = name?.trim();
  return trimmed || EMAIL_TEMPLATE_NAME_FALLBACK;
}

export function firstNameOrThere(
  name: string | null | undefined,
): string {
  return firstNonEmptyToken(name) ?? EMAIL_TEMPLATE_NAME_FALLBACK;
}

export function replaceEmailTemplateVariables(
  text: string,
  options: {
    recipientName?: string | null;
    email?: string | null;
    partyName?: string | null;
    inviteCode?: string | null;
    inviteUrl?: string | null;
    invitePageUrl?: string | null;
  } = {},
): string {
  const name = fullNameOrThere(options.recipientName);
  const firstName = firstNameOrThere(options.recipientName);
  const email = options.email?.trim() ?? "";
  const partyName = options.partyName?.trim() ?? "";
  const inviteCode = options.inviteCode?.trim() ?? "";
  const inviteUrl = options.inviteUrl?.trim() ?? "";
  const invitePageUrl = options.invitePageUrl?.trim() ?? "";

  return text
    .replace(/\{\{\s*name\s*\}\}/gi, () => name)
    .replace(/\{\{\s*first_name\s*\}\}/gi, () => firstName)
    .replace(/\{\{\s*email\s*\}\}/gi, () => email)
    .replace(/\{\{\s*party_name\s*\}\}/gi, () => partyName)
    .replace(/\{\{\s*invite_code\s*\}\}/gi, () => inviteCode)
    .replace(/\{\{\s*invite_url\s*\}\}/gi, () => inviteUrl)
    .replace(/\{\{\s*invite_page_url\s*\}\}/gi, () => invitePageUrl);
}

export function resolvePersonDisplayName(
  person: {
    name?: string | null;
    username?: string | null;
    email?: string | null;
  },
  fallback: string,
): string {
  const name = person.name?.trim();
  if (name) {
    return name;
  }

  const username = person.username?.trim();
  if (username) {
    return username;
  }

  const email = person.email?.trim();
  if (email) {
    return email;
  }

  return fallback;
}

export function resolvePersonInitial(
  person: {
    name?: string | null;
    username?: string | null;
    email?: string | null;
  },
  fallback = "?",
): string {
  const display = resolvePersonDisplayName(person, "");
  if (!display) {
    return fallback;
  }
  return display.charAt(0).toUpperCase();
}
